import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { ErrorBanner, Field, NativeSelect } from '@/components/form';
import { EmptyState, PageHeader } from '@/components/page-header';
import { toast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { fyStart } from '@/features/reports/settings';
import { parseUpliftPct, useBudgets, type Budget, type BudgetSummary } from '@/lib/accountant';
import { api } from '@/lib/api';
import { useMe } from '@/lib/clients';
import { todayIso } from '@/lib/dates';
import { formatAmount, formatDateTime } from '@/lib/format';
import { i18n, useT } from '@/lib/i18n';
import { useOrgId } from '@/lib/org';
import { useCompanySettings } from '@/lib/settings';

export const Route = createFileRoute('/accounting/budgets/')({ component: Budgets });

const addYears = (iso: string, n: number) => `${Number(iso.slice(0, 4)) + n}${iso.slice(4)}`;
const yearLabel = (start: string) =>
  i18n.t('accountingPages.budgetList.yearLabel', {
    year:
      start.slice(5, 7) === '01'
        ? start.slice(0, 4)
        : `${start.slice(0, 4)}/${String((Number(start.slice(0, 4)) + 1) % 100).padStart(2, '0')}`,
  });

/** Screen A11 (document 06): budgets, list pattern. */
function Budgets() {
  const orgId = useOrgId();
  const me = useMe();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const budgets = useBudgets();
  const t = useT();
  const isStaff = me.data?.isStaff ?? false;
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<BudgetSummary | null>(null);
  const remove = useMutation({
    mutationFn: (b: BudgetSummary) =>
      api<void>(`/orgs/${orgId}/budgets/${b.id}`, { method: 'DELETE' }),
    onSuccess: async (_v, b) => {
      await qc.invalidateQueries({ queryKey: ['budgets', orgId] });
      setDeleting(null);
      toast(t('accountingPages.budgetList.deleted', { name: b.name }));
    },
  });
  if (!orgId) return <EmptyState title={t('shell.selectClient')} />;
  const runReport = (b: BudgetSummary) =>
    void navigate({
      to: '/reports/$reportKey',
      params: { reportKey: 'budget-vs-actuals' },
      search: { budget: b.id, from: b.fiscalYearStart, to: lastDay(b.fiscalYearStart) },
    });
  return (
    <div>
      <PageHeader
        title={t('accountingPages.budgetList.title')}
        description={t('accountingPages.budgetList.description')}
        actions={
          isStaff ? (
            <Button onClick={() => setCreating(true)}>
              {t('accountingPages.budgetList.newBudget')}
            </Button>
          ) : null
        }
      />
      {budgets.isPending ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : budgets.isError ? (
        <EmptyState
          title={t('accountingPages.budgetList.couldNotLoad')}
          hint={budgets.error.message}
        />
      ) : !budgets.data.length ? (
        <EmptyState
          title={t('accountingPages.budgetList.emptyTitle')}
          hint={t('accountingPages.budgetList.emptyHint')}
          action={
            isStaff ? (
              <Button onClick={() => setCreating(true)}>
                {t('accountingPages.budgetList.newBudget')}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div className="rounded-lg border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('accountingPages.budgetList.col.name')}</TableHead>
                <TableHead>{t('accountingPages.budgetList.col.financialYear')}</TableHead>
                <TableHead className="text-end">
                  {t('accountingPages.budgetList.col.income')}
                </TableHead>
                <TableHead className="text-end">
                  {t('accountingPages.budgetList.col.expenses')}
                </TableHead>
                <TableHead className="text-end">
                  {t('accountingPages.budgetList.col.netIncome')}
                </TableHead>
                <TableHead>{t('accountingPages.budgetList.col.lastModified')}</TableHead>
                <TableHead className="w-[180px]">
                  {t('accountingPages.budgetList.col.action')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {budgets.data.map((b) => (
                <TableRow key={b.id} data-testid={`budget-${b.name}`}>
                  <TableCell>
                    <Link
                      to="/accounting/budgets/$budgetId"
                      params={{ budgetId: b.id }}
                      className="font-medium hover:text-primary hover:underline"
                    >
                      {b.name}
                    </Link>
                  </TableCell>
                  <TableCell>{b.fiscalYearLabel}</TableCell>
                  <TableCell className="tnum text-end">{formatAmount(b.total.income)}</TableCell>
                  <TableCell className="tnum text-end">{formatAmount(b.total.expense)}</TableCell>
                  <TableCell className="tnum text-end">{formatAmount(b.total.net)}</TableCell>
                  <TableCell>
                    {formatDateTime(b.updatedAt)}
                    {b.updatedBy ? (
                      <span className="text-muted-foreground"> · {b.updatedBy}</span>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center">
                      <Button
                        variant="link"
                        size="sm"
                        className="px-1"
                        onClick={() => runReport(b)}
                      >
                        {t('common.runReport')}
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            aria-label={t('accountingPages.budgetList.moreActions', {
                              name: b.name,
                            })}
                          >
                            <ChevronDown className="size-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onSelect={() =>
                              void navigate({
                                to: '/accounting/budgets/$budgetId',
                                params: { budgetId: b.id },
                              })
                            }
                          >
                            {isStaff ? t('common.edit') : t('accountingPages.budgetList.view')}
                          </DropdownMenuItem>
                          {isStaff ? (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                variant="destructive"
                                onSelect={() => setDeleting(b)}
                              >
                                {t('accountingPages.budgetList.delete')}
                              </DropdownMenuItem>
                            </>
                          ) : null}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <NewBudgetDialog
        open={creating}
        budgets={budgets.data ?? []}
        onClose={() => setCreating(false)}
        onCreated={(b) =>
          void navigate({ to: '/accounting/budgets/$budgetId', params: { budgetId: b.id } })
        }
      />
      <Dialog open={deleting !== null} onOpenChange={(o) => !o && setDeleting(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t('accountingPages.budgetList.deleteTitle', { name: deleting?.name ?? '' })}
            </DialogTitle>
            <DialogDescription>
              {t('accountingPages.budgetList.deleteDescription')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              disabled={remove.isPending}
              onClick={() => deleting && remove.mutate(deleting)}
            >
              {t('accountingPages.budgetList.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Last day of the twelve months starting on `start`. */
function lastDay(start: string): string {
  const y = Number(start.slice(0, 4));
  const m = Number(start.slice(5, 7));
  return new Date(Date.UTC(y + 1, m - 1, 0)).toISOString().slice(0, 10);
}

function NewBudgetDialog({
  open,
  budgets,
  onClose,
  onCreated,
}: {
  open: boolean;
  budgets: BudgetSummary[];
  onClose: () => void;
  onCreated: (b: Budget) => void;
}) {
  const orgId = useOrgId();
  const qc = useQueryClient();
  const company = useCompanySettings();
  const t = useT();
  const current = fyStart(todayIso(), company.data?.advanced.fiscalYearStart ?? 1);
  const years = [addYears(current, -1), current, addYears(current, 1)];
  const [name, setName] = useState('');
  const [year, setYear] = useState(current);
  const [from, setFrom] = useState<'blank' | 'actuals' | 'budget'>('actuals');
  const [sourceYear, setSourceYear] = useState(addYears(current, -1));
  const [sourceBudget, setSourceBudget] = useState('');
  const [uplift, setUplift] = useState('0');
  const upliftParsed = parseUpliftPct(uplift);
  const upliftPct = 'value' in upliftParsed ? upliftParsed.value : 0;
  const upliftError = from !== 'blank' && 'error' in upliftParsed ? upliftParsed.error : undefined;
  const create = useMutation({
    mutationFn: () =>
      api<Budget>(`/orgs/${orgId}/budgets`, {
        method: 'POST',
        body: JSON.stringify({
          name: name.trim() || t('accountingPages.budgetList.defaultName', { fy: yearLabel(year) }),
          fiscalYearStart: year,
          prefill:
            from === 'blank'
              ? { from }
              : from === 'actuals'
                ? { from, yearStart: sourceYear, upliftPct }
                : { from, budgetId: sourceBudget, upliftPct },
        }),
      }),
    onSuccess: async (b) => {
      await qc.invalidateQueries({ queryKey: ['budgets', orgId] });
      toast(t('accountingPages.budgetList.created', { name: b.name }));
      onClose();
      onCreated(b);
    },
  });
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          create.reset();
          onClose();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('accountingPages.budgetList.dialogTitle')}</DialogTitle>
          <DialogDescription>{t('accountingPages.budgetList.dialogDescription')}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <ErrorBanner>{create.error?.message ?? null}</ErrorBanner>
          <Field label={t('accountingPages.budgetList.name')}>
            {(id) => (
              <Input
                id={id}
                value={name}
                placeholder={t('accountingPages.budgetList.defaultName', { fy: yearLabel(year) })}
                onChange={(e) => setName(e.target.value)}
                maxLength={120}
              />
            )}
          </Field>
          <Field label={t('accountingPages.budgetList.financialYear')}>
            {(id) => (
              <NativeSelect id={id} value={year} onChange={(e) => setYear(e.target.value)}>
                {years.map((y) => (
                  <option key={y} value={y}>
                    {yearLabel(y)}
                  </option>
                ))}
              </NativeSelect>
            )}
          </Field>
          <Field label={t('accountingPages.budgetList.startFrom')}>
            {(id) => (
              <NativeSelect
                id={id}
                value={from}
                onChange={(e) => setFrom(e.target.value as 'blank' | 'actuals' | 'budget')}
              >
                <option value="actuals">{t('accountingPages.budgetList.fromActuals')}</option>
                <option value="budget" disabled={!budgets.length}>
                  {t('accountingPages.budgetList.fromBudget')}
                </option>
                <option value="blank">{t('accountingPages.budgetList.fromBlank')}</option>
              </NativeSelect>
            )}
          </Field>
          {from === 'actuals' ? (
            <Field label={t('accountingPages.budgetList.actualsOf')}>
              {(id) => (
                <NativeSelect
                  id={id}
                  value={sourceYear}
                  onChange={(e) => setSourceYear(e.target.value)}
                >
                  {[addYears(current, -2), ...years.slice(0, 2)].map((y) => (
                    <option key={y} value={y}>
                      {yearLabel(y)}
                    </option>
                  ))}
                </NativeSelect>
              )}
            </Field>
          ) : null}
          {from === 'budget' ? (
            <Field label={t('accountingPages.budgetList.budget')}>
              {(id) => (
                <NativeSelect
                  id={id}
                  value={sourceBudget}
                  onChange={(e) => setSourceBudget(e.target.value)}
                >
                  <option value="">{t('accountingPages.budgetList.chooseBudget')}</option>
                  {budgets.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </NativeSelect>
              )}
            </Field>
          ) : null}
          {from !== 'blank' ? (
            <Field
              label={t('accountingPages.budgetList.changeBy')}
              hint={t('accountingPages.budgetList.changeByHint')}
              error={upliftError}
            >
              {(id) => (
                <Input
                  id={id}
                  inputMode="decimal"
                  className="tnum w-32 text-end"
                  value={uplift}
                  aria-invalid={Boolean(upliftError) || undefined}
                  onChange={(e) => setUplift(e.target.value.replace(/[^0-9.-]/g, ''))}
                />
              )}
            </Field>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            disabled={
              create.isPending || (from === 'budget' && !sourceBudget) || Boolean(upliftError)
            }
            onClick={() => create.mutate()}
          >
            {create.isPending
              ? t('accountingPages.budgetList.creating')
              : t('accountingPages.budgetList.create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
